import React, {useEffect, useState} from 'react';
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useLoading} from "../../components/context/LoadingContext";
import {collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc} from "firebase/firestore";
import {
    db,
    deleteFileByUrl,
    eventsCollectionName,
    newsCollectionName,
    newsScheduledCollectionName,
    storage
} from "../../components/Firebase/Firebase";
import {getDownloadURL, ref, uploadBytes} from "firebase/storage";
import TextField from "@mui/material/TextField";
import {
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    FormControl, ImageList, ImageListItem,
    OutlinedInput,
    Select,
    Stack,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { CiImageOn } from "react-icons/ci";
import './NewsPage.css'
import { v4 as uuidv4 } from 'uuid';
import CustomDatePicker from "../../components/UI/datepicker/DatePicker";

const NewsEditPage = () => {
    const { newsId } = useParams();
    const location = useLocation();
    const isEditExisting = newsId !== 'new';
    const isScheduledSource = (location.state?.sourceCollection === newsScheduledCollectionName) || (location.state?.editSource === 'scheduled');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useLoading(false);

    const [newsItem, setNewsItem] = useState({
        title: '',
        id: '',
        mediaPreview: {
            type: 'video',
            url: null
        },
        sendPushToAllUsers: false
    });
    const [eventsIds, setEventsIds] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [oldUrls, setOldUrls] = useState([]);
    const [contentList, setContentList] = useState([]);
    // Publish controls
    const [publishMode, setPublishMode] = useState('now'); // 'now' | 'scheduled'
    const [publishAt, setPublishAt] = useState(''); // ISO string

    useEffect(() => {
        const initialize = async () => {
            if (newsId === 'new') {
                const newId = uuidv4();
                setNewsItem(prevState => ({ ...prevState, id: newId }));
                if (isScheduledSource) {
                    setPublishMode('scheduled');
                }
            } else {
                await fetchNewsItem(isScheduledSource);
            }
            await fetchEvents();
        };

        initialize();
    }, [newsId]);

    const onMediaPreviewFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            const fileType = file.type.startsWith('image/') ? 'image' : 'video';
            setNewsItem({
                ...newsItem,
                mediaPreview: {
                    ...newsItem.mediaPreview,
                    type: fileType,
                    url: null
                }
            });
        }
    };

    const uploadFile = async (file) => {
        const storageRef = ref(storage, `${newsCollectionName}/${newsItem.id}/` + file.name);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const handleAddContentToList = (updatedContentList) => {
        setContentList(updatedContentList);
    };

    const handleRemoveContentItem = (index) => {
        const newList = contentList.filter((item, itemIndex) => index !== itemIndex);
        setContentList(newList);
    };

    const handleTextChange = (index, newText) => {
        const updatedContentList = [...contentList];
        updatedContentList[index].content = newText;
        setContentList(updatedContentList);
    };

    const handleVideoFileChange = (event, index) => {
        const file = event.target.files[0];
        if (file) {
            const updatedContentList = [...contentList];
            updatedContentList[index].content = file;
            setContentList(updatedContentList);
        }
    };

    const handleImageFileChange = (event, index, fileIndex = null) => {
        const file = event.target.files[0];
        if (file) {
            const updatedContentList = [...contentList];
            const updatedFiles = [...updatedContentList[index].content];
            if (fileIndex !== null) {
                updatedFiles[fileIndex] = file;
            } else {
                updatedFiles.push(file);
            }
            updatedContentList[index].content = updatedFiles;
            setContentList(updatedContentList);
        }
    };

    const fetchNewsItem = async (isScheduledSource = false) => {
        setIsLoading(true);
        const sourceCollection = isScheduledSource ? newsScheduledCollectionName : newsCollectionName;
        const docRef = doc(db, sourceCollection, newsId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            setNewsItem(data);
            setContentList(data.contentList || []);
            if (data.associatedEvents) {
                setSelectedEvents(data.associatedEvents);
            } else {
                setSelectedEvents([]);
            }
            if (isScheduledSource) {
                setPublishMode('scheduled');
                setPublishAt(data.publishAt || '');
            }

            const tempUrls = [];

            if (data.mediaPreview && data.mediaPreview.url) {
                tempUrls.push(data.mediaPreview.url);
            }

            data.contentList.forEach(contentItem => {
                if (contentItem.contentType === 'imageList' && contentItem.content) {
                    tempUrls.push(...contentItem.content);
                } else if (contentItem.contentType === 'video' && contentItem.content) {
                    tempUrls.push(contentItem.content);
                }
            });

            setOldUrls(tempUrls);
        } else {
            console.error("No such document!");
        }
        setIsLoading(false);
    };

    const fetchEvents = async () => {
        setIsLoading(true);
        const querySnapshot = await getDocs(collection(db, eventsCollectionName));
        const fetchedEvents = querySnapshot.docs.map(doc => ({
            label: doc.data().title,
            value: doc.id
        }));
        setEventsIds(fetchedEvents);
        setIsLoading(false);
    };

    const handlePushToAllUsersStatusChange = (e) => {
        setNewsItem({...newsItem, sendPushToAllUsers: e.target.value === 'true'});
    };

    const handleUpdateNews = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (selectedFile) {
            const fileUrl = await uploadFile(selectedFile);
            newsItem.mediaPreview.url = fileUrl;
        } else {
            if (newsId === 'new') {
                newsItem.mediaPreview = null;
            }
        }

        const newContentList = [];

        for (const contentItem of contentList) {
            if (contentItem.contentType === 'video') {
                let fileUrl = typeof contentItem.content === 'string' ? contentItem.content : null;

                if (Array.isArray(contentItem.content) && contentItem.content[0] instanceof File) {
                    fileUrl = await uploadFile(contentItem.content[0]);
                } else if (contentItem.content instanceof File) {
                    fileUrl = await uploadFile(contentItem.content);
                }

                newContentList.push({ contentType: 'video', content: fileUrl });
            } else if (contentItem.contentType === 'imageList') {
                const imageUrls = await Promise.all(contentItem.content.map(file =>
                    file instanceof File ? uploadFile(file) : file
                ));

                newContentList.push({ contentType: 'imageList', content: imageUrls });
            } else {
                newContentList.push(contentItem);
            }
        }

        const associatedEvents = selectedEvents.length > 0 ? selectedEvents : null;

        const newNews = {
            ...newsItem,
            contentList: newContentList,
            associatedEvents: associatedEvents,
        };

        const tempNewUrls = [];

        if (newNews.mediaPreview && newNews.mediaPreview.url) {
            tempNewUrls.push(newNews.mediaPreview.url);
        }

        newNews.contentList.forEach(contentItem => {
            if (contentItem.contentType === 'imageList' && contentItem.content) {
                tempNewUrls.push(...contentItem.content);
            } else if (contentItem.contentType === 'video' && contentItem.content) {
                tempNewUrls.push(contentItem.content);
            }
        });

        console.log('New: ', tempNewUrls);
        console.log('Old: ', oldUrls);

        try {
            const canSchedule = !(isEditExisting && !isScheduledSource);
            if (publishMode === 'scheduled' && canSchedule) {
                // Validate publishAt
                if (!publishAt) {
                    throw new Error('Не указаны дата/время публикации');
                }
                const publishAtUtc = new Date(publishAt).toISOString();
                // For scheduled, write to a staging collection with publishAt
                const scheduledDoc = {
                    ...newNews,
                    publishAt: publishAtUtc,
                };
                if (newsId === 'new') {
                    await setDoc(doc(db, newsScheduledCollectionName, scheduledDoc.id), scheduledDoc);
                    console.log('Scheduled news written with ID: ', scheduledDoc.id);
                } else {
                    // Detect source via outer flag; for published items we don't allow scheduling in UI
                    if (isScheduledSource) {
                        await updateDoc(doc(db, newsScheduledCollectionName, newsId), scheduledDoc);
                        console.log('Scheduled news updated with ID: ', newsId);
                    }
                }
            } else {
                // Publish now: use existing behavior with news collection
                if (newsId === 'new') {
                    await setDoc(doc(db, newsCollectionName, newNews.id), newNews);
                    console.log('Document written with ID: ', newNews.id);
                } else {
                    if (isScheduledSource) {
                        // Move from scheduled to published
                        await setDoc(doc(db, newsCollectionName, newsId), newNews);
                        await deleteDoc(doc(db, newsScheduledCollectionName, newsId));
                        console.log('News published and scheduled doc removed: ', newsId);
                    } else {
                        await updateDoc(doc(db, newsCollectionName, newsId), newNews);
                        console.log('News updated with ID: ', newsId);
                    }
                }
            }

            // After any update of an existing item (scheduled or published), clean up orphaned files
            if (newsId !== 'new') {
                const urlsToDelete = oldUrls.filter(url => !tempNewUrls.includes(url));
                if (urlsToDelete.length > 0) {
                    console.log('Deleting orphaned files: ', urlsToDelete);
                    for (const url of urlsToDelete) {
                        await deleteFileByUrl(url);
                    }
                }
            }
        } catch (error) {
            console.error('Error adding/updating document: ', error);
        }

        setIsLoading(false);
        navigate('/news');
    };

    return (
        <div className='right-content'>
            <form onSubmit={handleUpdateNews}>
                <div className='card shadow border-0'>
                    <div className='form-group'>
                        <h4 className='hd'>Новость: {newsItem.id}</h4>
                    </div>
                    <div className='row form-group'>
                        <div className='col'>
                            <TextField
                                className='w-100'
                                value={newsItem.title || ''}
                                onChange={e => setNewsItem({...newsItem, title: e.target.value})}
                                label="Название"
                                multiline
                                rows={2}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '& input': {
                                            color: '#fff',
                                        },
                                        '& textarea': {
                                            color: '#fff',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#fff',
                                        '&.Mui-focused': {
                                            color: '#fff',
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>
                <div className='w-25' style={{marginTop: 10}}>
                    <CustomDatePicker
                        label="Дата"
                        value={newsItem.date || ''}
                        onChange={(isoDate) => setNewsItem({...newsItem, date: isoDate})}
                    />
                </div>
                <div className='form-group'>
                    <div style={{marginTop: 10}}>
                        <h5>Публикация:</h5>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                        <FormControl
                            className="w-auto"
                            sx={{
                                minWidth: 200,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#CEFC4C' },
                                    '&:hover fieldset': { borderColor: '#CEFC4C' },
                                    '&.Mui-focused fieldset': { borderColor: '#CEFC4C' },
                                },
                            }}
                        >
                            <Select
                                value={publishMode}
                                onChange={(e) => setPublishMode(e.target.value)}
                                input={<OutlinedInput/>}
                                sx={{
                                    color: '#fff',
                                    '& .MuiSvgIcon-root': { color: '#fff' },
                                }}
                            >
                                <MenuItem value="now">Сейчас</MenuItem>
                                {(newsId === 'new' || isScheduledSource) && (
                                    <MenuItem value="scheduled">Запланировать</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        {publishMode === 'scheduled' && (newsId === 'new' || isScheduledSource) && (
                            <div className='w-25'>
                                <CustomDatePicker
                                    label="Время публикации"
                                    value={publishAt}
                                    onChange={(iso) => setPublishAt(iso)}
                                />
                            </div>
                        )}
                    </div>
                </div>
                    <div className='form-group'>
                        <div style={{marginTop: 10}}>
                            <h5>Отправить пуш уведомление всем пользователям: </h5>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <FormControl
                                className="w-auto"
                                sx={{
                                    minWidth: 200,
                                    marginRight: 5,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                    },
                                }}
                            >
                                <Select
                                    value={(newsItem.sendPushToAllUsers ?? false).toString()}
                                    onChange={handlePushToAllUsersStatusChange}
                                    input={<OutlinedInput/>}
                                    sx={{
                                        color: '#fff',
                                        '& .MuiSvgIcon-root': {
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    <MenuItem value="false">Нет</MenuItem>
                                    <MenuItem value="true">Да</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                    <div className='form-group'>
                        <div style={{marginTop: 10}}>
                            <h5>Связанные события:</h5>
                        </div>
                        <FormControl
                            className="w-100"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#CEFC4C',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#CEFC4C',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#CEFC4C',
                                    },
                                },
                            }}
                        >
                            <Select
                                multiple
                                value={selectedEvents}
                                onChange={(e) => setSelectedEvents(e.target.value)}
                                input={<OutlinedInput/>}
                                sx={{
                                    '& .MuiSvgIcon-root': {
                                        color: '#fff',
                                    },
                                }}
                                renderValue={(selected) => (
                                    <Stack gap={1} direction="row" flexWrap="wrap">
                                        {selected.map((value) => (
                                            <Chip
                                                className='eventsMultiselect'
                                                key={value}
                                                label={eventsIds.find(event => event.value === value)?.label || value}
                                                onDelete={() =>
                                                    setSelectedEvents(
                                                        selectedEvents.filter((item) => item !== value)
                                                    )
                                                }
                                                deleteIcon={
                                                    <CancelIcon
                                                        onMouseDown={(event) => event.stopPropagation()}
                                                    />
                                                }
                                            />
                                        ))}
                                    </Stack>
                                )}
                            >
                                {eventsIds.map((event) => (
                                    <MenuItem
                                        key={event.value}
                                        value={event.value}
                                        sx={{justifyContent: "space-between"}}
                                    >
                                        {event.label}
                                        {selectedEvents.includes(event.value) ? <CheckIcon color="info"/> : null}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className='form-group'>
                        <div style={{marginTop: 10}}>
                            <h5>Медиа превью:</h5>
                        </div>
                        <div style={{width: 312}}>
                            <Card
                                sx={{width: 312, height: 214}}
                                className='mediaCard'
                            >
                                {selectedFile !== null || (newsItem.mediaPreview && newsItem.mediaPreview.url) ? (
                                    newsItem.mediaPreview.type === 'image' ? (
                                        <CardMedia
                                            component="img"
                                            sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                            src={selectedFile !== null ? URL.createObjectURL(selectedFile) : newsItem.mediaPreview.url}
                                            alt="media preview"
                                        />
                                    ) : (
                                        <CardMedia
                                            component="video"
                                            sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                            src={selectedFile !== null ? URL.createObjectURL(selectedFile) : newsItem.mediaPreview.url}
                                            controls
                                            alt="media preview"
                                        />
                                    )
                                ) : (
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CiImageOn size={40}/>
                                    </CardContent>
                                )}
                            </Card>
                            <Button
                                className='logInButton w-100'
                                variant="contained"
                                component="label"
                                sx={{marginTop: 2}}
                            >
                                Изменить
                                <input
                                    type="file"
                                    hidden
                                    onChange={onMediaPreviewFileChange}
                                />
                            </Button>
                        </div>
                    </div>

                    <div>
                        <ContentInput
                            onAddContent={handleAddContentToList}
                            contentList={contentList}
                        />
                        <div style={{marginTop: 10}}>
                            <h5>Список контента:</h5>
                        </div>
                        {
                            contentList.map((contentItem, index) => (
                                <div key={index}>
                                    <Button
                                        style={{
                                            marginBottom: 10,
                                            marginTop: 15,
                                            textTransform: 'capitalize',
                                            backgroundColor: '#CEFC4C',
                                            color: '#000',
                                            fontSize: 16
                                        }}
                                        variant="contained"
                                        component="label"
                                        type="button"
                                        onClick={() => handleRemoveContentItem(index)}
                                    >
                                        Удалить
                                    </Button>
                                    <div>
                                        {contentItem.contentType === 'text' && (
                                            <TextField
                                                style={{width: '100%', overflowY: 'auto'}}
                                                className='w-100'
                                                value={contentItem.content}
                                                onChange={(e) => handleTextChange(index, e.target.value)}
                                                multiline
                                                rows={4}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#CEFC4C',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#CEFC4C',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#CEFC4C',
                                                        },
                                                        '& input': {
                                                            color: '#fff',
                                                        },
                                                        '& textarea': {
                                                            color: '#fff',
                                                        },
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: '#fff',
                                                        '&.Mui-focused': {
                                                            color: '#fff',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        {contentItem.contentType === 'video' && (
                                            <div style={{width: 312}}>
                                                <Card
                                                    sx={{width: 312, height: 214}}
                                                    className='mediaCard'
                                                >
                                                    {(() => {
                                                        const content = Array.isArray(contentItem.content) ? contentItem.content[0] : contentItem.content;
                                                        console.log('content:', content);
                                                        return typeof content === 'string' ? (
                                                            <CardMedia
                                                                component="video"
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain'
                                                                }}
                                                                src={content}
                                                                controls
                                                                alt="video preview"
                                                            />
                                                        ) : (
                                                            <CardMedia
                                                                component="video"
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain'
                                                                }}
                                                                src={URL.createObjectURL(content)}
                                                                controls
                                                                alt="video preview"
                                                            />
                                                        );
                                                    })()}
                                                </Card>
                                                <Button
                                                    className='logInButton w-100'
                                                    variant="contained"
                                                    component="label"
                                                    sx={{marginTop: 2}}
                                                >
                                                    Изменить
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => handleVideoFileChange(e, index)}
                                                    />
                                                </Button>
                                            </div>
                                        )}
                                        {contentItem.contentType === 'imageList' && (
                                            <ImageList cols={50} gap={20}>
                                                {contentItem.content.map((file, fileIndex) => (
                                                    <ImageListItem key={fileIndex} style={{width: 312}}>
                                                        <Card
                                                            sx={{width: '100%', height: 214}}
                                                            className='mediaCard'
                                                        >
                                                            <CardMedia
                                                                component="img"
                                                                sx={{width: '100%', height: '100%', objectFit: 'cover'}}
                                                                src={typeof file === 'string' ? file : URL.createObjectURL(file)}
                                                                alt="image preview"
                                                            />
                                                        </Card>
                                                        <Button
                                                            className='logInButton w-100'
                                                            variant="contained"
                                                            component="label"
                                                            sx={{marginTop: 2}}
                                                        >
                                                            Изменить
                                                            <input
                                                                type="file"
                                                                hidden
                                                                onChange={(e) => handleImageFileChange(e, index, fileIndex)}
                                                            />
                                                        </Button>
                                                    </ImageListItem>
                                                ))}
                                                <ImageListItem style={{width: 312, height: 214}}>
                                                    <Card
                                                        sx={{width: '100%', height: '100%'}}
                                                        className='mediaCard'
                                                        onClick={() => document.getElementById(`file-upload-input-${index}`).click()}
                                                    >
                                                        <CardContent
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                height: '100%',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <CiImageOn size={40}/>
                                                        </CardContent>
                                                    </Card>
                                                    <input
                                                        id={`file-upload-input-${index}`}
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => handleImageFileChange(e, index)}
                                                    />
                                                </ImageListItem>
                                            </ImageList>
                                        )}
                                    </div>
                                </div>
                            ))
                        }
                    </div>

                    <Button
                        className='logInButton w-100'
                        variant="contained"
                        component="label"
                        type="button"
                        onClick={handleUpdateNews}
                    >
                        Сохранить
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default NewsEditPage;

const ContentInput = ({onAddContent, contentList}) => {
    const [contentType, setContentType] = useState('text');
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const [insertIndex, setInsertIndex] = useState('в конец');

    const handleAddContent = () => {
        let updatedContentList = [...contentList];
        const newContentItem = {
            contentType: contentType === 'text' ? 'text' : (files[0].type.startsWith('image/') ? "imageList" : "video"),
            content: contentType === 'text' ? text : files
        };

        if (insertIndex === 'в конец') {
            updatedContentList.push(newContentItem);
        } else {
            updatedContentList.splice(Number(insertIndex), 0, newContentItem);
        }

        onAddContent(updatedContentList);

        if (contentType === 'text') {
            setText('');
        } else {
            setFiles([]);
        }
    };

    const onFileChange = (event, index) => {
        const file = event.target.files[0];
        if (file) {
            const newFiles = [...files];
            if (index !== null) {
                newFiles[index] = file;
            } else {
                newFiles.push(file);
            }
            setFiles(newFiles);
        }
    };

    return (
        <div>
            <div style={{marginTop: 10}}>
                <h5>Добавить контент:</h5>
            </div>
            <div style={{display: 'flex', alignItems: 'center'}}>
                <FormControl
                    className="w-auto"
                    sx={{
                        minWidth: 200,
                        marginRight: 5,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#CEFC4C',
                            },
                            '&:hover fieldset': {
                                borderColor: '#CEFC4C',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#CEFC4C',
                            },
                        },
                    }}
                >
                    <Select
                        value={contentType}
                        onChange={(e) => setContentType(e.target.value)}
                        input={<OutlinedInput/>}
                        sx={{
                            color: '#fff',
                            '& .MuiSvgIcon-root': {
                                color: '#fff',
                            },
                        }}
                    >
                        <MenuItem value="text">Текст</MenuItem>
                        <MenuItem value="media">Медиа</MenuItem>
                    </Select>
                </FormControl>
                <FormControl
                    sx={{
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#CEFC4C',
                            },
                            '&:hover fieldset': {
                                borderColor: '#CEFC4C',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#CEFC4C',
                            },
                        },
                    }}
                >
                    <Select
                        value={insertIndex}
                        onChange={(e) => setInsertIndex(e.target.value)}
                        input={<OutlinedInput/>}
                        sx={{
                            color: '#fff',
                            '& .MuiSvgIcon-root': {
                                color: '#fff',
                            },
                        }}
                    >
                        {Array.isArray(contentList) && contentList.map((_, index) => (
                            <MenuItem key={index} value={index}>{index + 1}</MenuItem>
                        ))}
                        <MenuItem value="в конец">В конец</MenuItem>
                    </Select>
                </FormControl>
            </div>
            {contentType === 'text' && (
                <div style={{marginTop: 20}}>
                    <TextField
                        style={{width: '100%', overflowY: 'auto'}}
                        className='w-100'
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        multiline
                        rows={4}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#CEFC4C',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#CEFC4C',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#CEFC4C',
                                },
                                '& input': {
                                    color: '#fff',
                                },
                                '& textarea': {
                                    color: '#fff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#fff',
                                '&.Mui-focused': {
                                    color: '#fff',
                                },
                            },
                        }}
                    />
                </div>
            )}
            {(contentType === 'media') && (
                <div style={{marginTop: 20}}>
                    <ImageList cols={50} gap={20}>
                        {files.map((file, index) => (
                            <ImageListItem key={index} style={{width: 312}}>
                                <Card
                                    sx={{width: '100%', height: 214}}
                                    className='mediaCard'
                                >
                                    {file ? (
                                        file.type.startsWith('image/') ? (
                                            <CardMedia
                                                component="img"
                                                sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                                src={URL.createObjectURL(file)}
                                                alt="media preview"
                                            />
                                        ) : (
                                            <CardMedia
                                                component="video"
                                                sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                                src={URL.createObjectURL(file)}
                                                controls
                                                alt="media preview"
                                            />
                                        )
                                    ) : (
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                            }}
                                        >
                                            <CiImageOn size={40}/>
                                        </CardContent>
                                    )}
                                </Card>
                                <Button
                                    className='logInButton w-100'
                                    variant="contained"
                                    component="label"
                                    sx={{marginTop: 2}}
                                >
                                    Изменить
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) => onFileChange(e, index)}
                                    />
                                </Button>
                            </ImageListItem>
                        ))}
                        <ImageListItem style={{width: 312, height: 214}}>
                            <Card
                                sx={{width: '100%', height: '100%'}}
                                className='mediaCard'
                                onClick={() => document.getElementById('file-upload-input').click()}
                            >
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <CiImageOn size={40}/>
                                </CardContent>
                            </Card>
                            <input
                                id="file-upload-input"
                                type="file"
                                hidden
                                onChange={(e) => onFileChange(e, null)}
                            />
                        </ImageListItem>
                    </ImageList>
                </div>
            )}
            <div>
                <Button
                    className='logInButton'
                    variant="contained"
                    component="label"
                    type="button"
                    onClick={handleAddContent}
                >
                    Добавить контент
                </Button>
            </div>
        </div>
    );
};
